<template>
  <section class="flex flex-col gap-4 rounded-lg border p-4">
    <header class="flex items-center justify-between gap-3">
      <h3 class="text-base font-semibold text-slate-900">
        {{ titleLabel }}
      </h3>
      <span class="text-xs text-slate-500">{{ countLabel }}</span>
    </header>

    <div v-if="replyTargetNote && !readonly" class="flex flex-col gap-2 rounded-md border border-slate-300 bg-slate-100 p-3">
      <div class="flex items-center justify-between gap-3">
        <p class="text-xs font-medium text-slate-700">
          {{ replyingToLabel }}
        </p>
        <button class="rounded-md border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 transition hover:bg-slate-200" type="button" @click="clearReplyTarget">
          {{ cancelReplyLabel }}
        </button>
      </div>
      <blockquote class="rounded-md border-l-4 border-slate-300 bg-white px-3 py-2 text-xs text-slate-600">
        {{ replyTargetNote.contents }}
      </blockquote>
    </div>

    <NoteAddForm
      v-if="!readonly"
      ref="noteAddFormRef"
      :loading="notesStore.isLoading"
      :placeholder="formPlaceholderLabel"
      :submit-label="formSubmitLabel"
      :submitting-label="formSubmittingLabel"
      :attach-button-label="attachButtonLabel"
      :attachments-label="attachmentsLabel"
      :remove-attachment-label="removeAttachmentLabel"
      :upload-error-label="uploadErrorLabel"
      @submit="createNote"
    />

    <p v-if="errorMessage" class="text-sm text-red-600">
      {{ errorMessage }}
    </p>

    <div class="flex flex-col gap-3">
      <NoteCard
        v-for="item in notesStore.items"
        :key="item.id"
        :note="item"
        :deleting="notesStore.isLoadingItems.includes(item.id)"
        :readonly="readonly"
        @reply="setReplyTarget"
        @delete="deleteNote"
      />
      <p v-if="!notesStore.isLoading && notesStore.items.length === 0" class="text-sm text-slate-500">
        {{ emptyLabel }}
      </p>
    </div>
  </section>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useNotesStore } from '../../stores/notesStore.js'
import NoteCard from './NoteCard.vue'
import NoteAddForm from './NoteAddForm.vue'

const props = defineProps({
  id: {
    type: [Number, String],
    required: true,
  },
  noteable: {
    type: String,
    required: true,
  },
  type: {
    type: String,
    default: 'note',
  },
  readonly: {
    type: Boolean,
    default: false,
  },
})

const notesStore = useNotesStore(`noteable_${String(props.noteable).replace(/[^A-Za-z0-9]/g, '_')}_${props.id}`)
const { t, te } = useI18n()
const replyTargetNote = ref(null)
const noteAddFormRef = ref(null)

const normalizedType = computed(() => {
  const value = String(props.type ?? 'note')
    .trim()
    .toLowerCase()

  if (value.length === 0) {
    return 'note'
  }

  return value
})

const conceptSingular = computed(() => {
  const key = `notes.concepts.${normalizedType.value}.singular`

  if (te(key)) {
    return t(key)
  }

  return normalizedType.value
})

const conceptPlural = computed(() => {
  const key = `notes.concepts.${normalizedType.value}.plural`

  if (te(key)) {
    return t(key)
  }

  return `${normalizedType.value}s`
})

const conceptTitle = computed(() => {
  const key = `notes.concepts.${normalizedType.value}.title`

  if (te(key)) {
    return t(key)
  }

  const singular = conceptSingular.value

  return singular.charAt(0).toUpperCase() + singular.slice(1)
})

const titleLabel = computed(() => t('notes.card.title', { title: conceptTitle.value }))
const countLabel = computed(() =>
  t('notes.card.count', {
    count: notesStore.items.length,
    conceptPlural: conceptPlural.value,
  }),
)
const emptyLabel = computed(() => t('notes.card.empty', { conceptPlural: conceptPlural.value }))
const formPlaceholderLabel = computed(() => t('notes.card.formPlaceholder', { conceptSingular: conceptSingular.value }))
const formSubmitLabel = computed(() => t('notes.card.formSubmit', { conceptSingular: conceptSingular.value }))
const formSubmittingLabel = computed(() => t('notes.card.formSubmitting', { conceptSingular: conceptSingular.value }))
const attachButtonLabel = computed(() => t('notes.card.attachButton'))
const attachmentsLabel = computed(() => t('notes.card.attachmentsLabel'))
const removeAttachmentLabel = computed(() => t('notes.card.removeAttachment'))
const uploadErrorLabel = computed(() => t('notes.card.uploadError'))
const replyingToLabel = computed(() => t('notes.card.replyingTo', { conceptSingular: conceptSingular.value }))
const cancelReplyLabel = computed(() => t('notes.card.replyCancel'))
const deleteConfirmLabel = computed(() => t('notes.card.deleteConfirm', { conceptSingular: conceptSingular.value }))

const errorMessage = computed(() => {
  if (!notesStore.error) {
    return null
  }

  return (
    notesStore.error.response?.data?.message ??
    notesStore.error.message ??
    t('notes.card.errorLoad', {
      conceptPlural: conceptPlural.value,
    })
  )
})

const loadNotes = async () => {
  await notesStore.fetchItemsForNoteable(props.id, props.noteable, {
    type: normalizedType.value,
    not_paginated: true,
    order_by: 'created_at',
    order_direction: 'desc',
  })
}

const createNote = async (payload) => {
  if (props.readonly) {
    return
  }

  if (replyTargetNote.value?.id) {
    await notesStore.replyToNote(replyTargetNote.value.id, {
      ...payload,
      type: normalizedType.value,
    })
  } else {
    await notesStore.createForActiveNoteable({
      ...payload,
      type: normalizedType.value,
    })
  }

  clearReplyTarget()
  await loadNotes()
}

const setReplyTarget = (note) => {
  if (props.readonly) {
    return
  }

  if (!note || typeof note !== 'object') {
    return
  }

  replyTargetNote.value = note
  focusAddForm()
}

const clearReplyTarget = () => {
  replyTargetNote.value = null
}

const focusAddForm = async () => {
  await nextTick()
  noteAddFormRef.value?.focusInput?.()
}

const deleteNote = async (note) => {
  if (props.readonly) {
    return
  }

  if (!note || typeof note !== 'object' || !note.id) {
    return
  }

  if (!window.confirm(deleteConfirmLabel.value)) {
    return
  }

  await notesStore.itemDelete(note.id)

  if (replyTargetNote.value?.id === note.id) {
    clearReplyTarget()
  }
}

watch(
  () => [props.id, props.noteable, props.type],
  async () => {
    clearReplyTarget()
    await loadNotes()
  },
)

watch(
  () => props.readonly,
  (readonly) => {
    if (readonly) {
      clearReplyTarget()
    }
  },
)

onMounted(async () => {
  await loadNotes()
})
</script>
